import { InputChangeEventDetail } from '@ionic/core/components';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonList,
  IonPopover,
  IonRow,
} from '@ionic/react';
import { trashBinOutline as deleteIcon, ellipsisVertical as menuIcon } from 'ionicons/icons';
import React, { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';

import { MaterialProps } from './MaterialModal';

const useStyles = createUseStyles({
  root: {
    '--inner-padding-start': '5px',
    '--inner-padding-end': '0px',
    fontSize: '0.875rem',
    lineHeight: 1.2,

    '& ion-label p': {
      lineHeight: 1.2,
    },
  },
});

type MaterialFormProps = {
  material: MaterialProps;
  handleRemove: (props: MaterialProps) => void;
  handleUpdate: (props: MaterialProps) => void;
  handleUpdateUnitCostAll: (props: MaterialProps) => void;
  handleUpdateUnitCostEmpty: (props: MaterialProps) => void;
  handleUpdateQuantityAll: (props: MaterialProps) => void;
  handleUpdateQuantityEmpty: (props: MaterialProps) => void;
};

const MaterialForm: React.FC<MaterialFormProps> = ({
  material,
  handleRemove,
  handleUpdate,
  handleUpdateUnitCostAll,
  handleUpdateUnitCostEmpty,
  handleUpdateQuantityAll,
  handleUpdateQuantityEmpty,
}) => {
  const classes = useStyles();
  const itemSlidingRef = useRef<HTMLIonItemSlidingElement>(null);
  const [popoverState, setPopoverState] = React.useState<{
    showPopup: boolean;
    event: CustomEvent<InputChangeEventDetail> | undefined;
  }>({
    showPopup: false,
    event: undefined,
  });

  const closePopover = () => {
    setPopoverState({
      showPopup: false,
      event: undefined,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <IonItemSliding ref={itemSlidingRef}>
      <IonItemOptions side="start">
        <IonItemOption color="danger" onClick={() => handleRemove(material)}>
          <IonIcon slot="icon-only" icon={deleteIcon} />
        </IonItemOption>
      </IonItemOptions>
      <IonItem lines="full" detail={false} className={classes.root}>
        <IonGrid style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}>
          <IonRow className="ion-align-items-center ion-no-padding" style={{ width: '100%' }}>
            <IonCol size="4" className="ion-text-left">
              <IonInput
                value={material.name}
                type="text"
                placeholder="Name"
                onIonChange={(e) =>
                  handleUpdate({
                    ...material,
                    name: e.detail.value ? e.detail.value : undefined,
                  })
                }
              />
            </IonCol>
            <IonCol className="ion-text-center" size="2">
              <IonInput
                value={material.unitCost}
                type="number"
                step="1"
                inputMode="decimal"
                placeholder="Cost"
                onIonChange={(e) =>
                  handleUpdate({
                    ...material,
                    unitCost: e.detail.value ? e.detail.value : undefined,
                  })
                }
              />
            </IonCol>
            <IonCol className="ion-text-center" size="2">
              <IonInput
                value={material.quantity}
                type="number"
                step="1"
                inputMode="decimal"
                placeholder="Quantity"
                onIonChange={(e) =>
                  handleUpdate({
                    ...material,
                    quantity: e.detail.value ? e.detail.value : undefined,
                  })
                }
              />
            </IonCol>
            <IonCol size="4" className="ion-text-center">
              <IonInput
                value={material.referenceNumber}
                type="text"
                placeholder="Ref #"
                onIonChange={(e) =>
                  handleUpdate({
                    ...material,
                    referenceNumber: e.detail.value ? e.detail.value : undefined,
                  })
                }
              />
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonButtons slot="end">
          <IonButton
            fill="clear"
            tabIndex={-1}
            onClick={(e: any) => setPopoverState({ showPopup: true, event: e })}
          >
            <IonIcon slot="icon-only" color="primary" icon={menuIcon} size="small" />
            <IonPopover
              mode="md"
              showBackdrop={false}
              keyboardClose={false}
              event={popoverState.event}
              isOpen={popoverState.showPopup}
              onDidDismiss={() => closePopover()}
            >
              <IonContent>
                <IonList style={{ padding: 0 }}>
                  <IonItem
                    button={true}
                    disabled={!material.unitCost}
                    onClick={() => {
                      closePopover();
                      handleUpdateUnitCostEmpty(material);
                    }}
                  >
                    Apply cost to all empty
                  </IonItem>
                  <IonItem
                    button={true}
                    disabled={!material.unitCost}
                    onClick={() => {
                      handleUpdateUnitCostAll(material);
                      closePopover();
                    }}
                  >
                    Apply cost to all
                  </IonItem>
                  <IonItem
                    button={true}
                    disabled={!material.quantity}
                    onClick={() => {
                      handleUpdateQuantityEmpty(material);
                      closePopover();
                    }}
                  >
                    Apply units to all empty
                  </IonItem>
                  <IonItem
                    button={true}
                    disabled={!material.quantity}
                    onClick={() => {
                      handleUpdateQuantityAll(material);
                      closePopover();
                    }}
                  >
                    Apply units to all
                  </IonItem>
                </IonList>
              </IonContent>
            </IonPopover>
          </IonButton>
        </IonButtons>
      </IonItem>
    </IonItemSliding>
  );
};

export default MaterialForm;
